import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Typography } from "@mui/material";
import {
  EnterprisePackageServiceSettingsDto,
  SaveServicePackageServiceVariant,
  ServiceListItemDto,
  ServicePackageDto,
  ServiceVariantFrequency,
} from "Api/Api";
import {
  PriceColumnsHeader,
  PriceColumnsRow,
  StyledPriceGrid,
} from "Components/Settings/Services/PriceColumns";
import {
  FormModel,
  VariantFormModel,
  useValidationSchema,
} from "Components/Settings/Services/ServicePackages/Details/useValidationSchema";
import { BlDefaultTooltip } from "Components/Shared/BlTooltipDefault";
import { BlButton } from "Components/Shared/Buttons/BlButton";
import { BlConfirmButton } from "Components/Shared/Buttons/BlConfirmButton";
import { StyledValidationText } from "Components/Shared/FormStyles";
import { BlCheckboxInput } from "Components/Shared/Inputs/Form/BlCheckboxInput";
import { BlFormInput } from "Components/Shared/Inputs/Form/BlFormInput";
import {
  StyledContentWrapper,
  StyledFlex,
} from "Components/Shared/StyledComponents";
import { PriceTaxType } from "Constants/PriceTaxType";
import { calculatePriceByPriceTaxTax } from "Hooks/useWithTax";
import { CurrencyCode } from "Models/CurrencyCodes";
import { deleteServicePackageAsync } from "State/ServicePackages/Slices/DeleteServicePackageSlice";
import { saveServicePackageAsync } from "State/ServicePackages/Slices/SaveServicePackage";
import { useAppSelector } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import { AppRouting, getPath } from "Utils/UrlUtils";
import React from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import styled from "styled-components";

const FormWrapper = styled(Box)``;

const PageResources = Resources.Settings.ServicePackages.Detail.Form;

const MAX_PRICE_WITHOUT_TAX = 999999;

type Props = {
  servicePackage: ServicePackageDto | null;
  availableServices: ServiceListItemDto[];
  serviceSettings: EnterprisePackageServiceSettingsDto;
};

export const ServicePackageForm: React.FunctionComponent<Props> = props => {
  const { servicePackage, availableServices, serviceSettings } = props;

  const { t } = useResource();

  const validationSchema = useValidationSchema(serviceSettings);

  const form = useForm<FormModel>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: servicePackage?.name ?? "",
      description: servicePackage?.description ?? "",
      services: availableServices.map(x => ({
        serviceID: x.serviceID,
        isSelected:
          servicePackage?.relatedServices.some(
            y => y.serviceID === x.serviceID,
          ) ?? false,
        name: x.name,
        description: x.description,
      })),
      variants: getVariants().map(x => {
        const variant = servicePackage?.variants.find(y => y.name === x.name);
        const taxRate = variant?.taxRate ?? x.taxRate;
        const priceWithoutTax = variant?.priceWithoutTax ?? 0;

        return {
          ...x,
          taxRate,
          priceWithoutTax,
          // the stored gross price is always re-derived from the net one
          priceWithTax: calculatePriceByPriceTaxTax(
            priceWithoutTax,
            taxRate,
            PriceTaxType.WithoutTax,
          ),
          isSelected: !!variant,
          serviceVariantID: variant?.serviceVariantID ?? undefined,
        };
      }),
    },
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    trigger,
    setValue,
    getValues,
  } = form;

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { isLoading: isSaving } = useAppSelector(x => x.servicePackage.save);

  const onSubmit = async () => {
    await trigger();

    handleSubmit((formModel: FormModel) => {
      dispatch(
        saveServicePackageAsync.request({
          request: {
            name: formModel.name,
            description: formModel.description,
            relatedServices: formModel.services
              .filter(x => x.isSelected)
              .map(x => ({
                serviceID: x.serviceID,
              })),
            variants: formModel.variants
              .filter(x => x.isSelected)
              .map(x => {
                const variant: SaveServicePackageServiceVariant = {
                  currencyCode: CurrencyCode.CZK,
                  frequency: x.frequency,
                  isEnabled: x.isSelected,
                  name: x.name,
                  priceWithoutTax: x.priceWithoutTax!,
                  taxRate: x.taxRate,
                  priceWithTax: x.priceWithTax!,
                  serviceVariantID: x.serviceVariantID!,
                };

                return variant;
              }),
          },
          servicePublicID: servicePackage?.servicePublicID ?? null,
          navigate,
        }),
      );
    })();
  };

  const onPriceWithoutTaxChange = (i: number) => {
    const priceWithTax = calculatePriceByPriceTaxTax(
      getValues(`variants.${i}.priceWithoutTax`),
      getValues(`variants.${i}.taxRate`),
      PriceTaxType.WithoutTax,
    );
    setValue(`variants.${i}.priceWithTax`, priceWithTax);
  };

  const onPriceWithTaxChange = (i: number) => {
    const priceWithoutTax = calculatePriceByPriceTaxTax(
      getValues(`variants.${i}.priceWithTax`),
      getValues(`variants.${i}.taxRate`),
      PriceTaxType.WithTax,
    );
    setValue(`variants.${i}.priceWithoutTax`, priceWithoutTax);
  };

  return (
    <StyledContentWrapper>
      <FormWrapper>
        <Typography variant="h2">{t(PageResources.Title)}</Typography>

        <Typography variant="subtitle2" fontStyle={"normal"} mb={2}>
          {t(PageResources.FormDescription)}
        </Typography>

        <form>
          <BlFormInput
            name={"name"}
            label={t(PageResources.Name.Label)}
            placeholder={t(PageResources.Name.Placeholder)}
            control={control}
            errors={errors}
            fullWidth={false}
            maxLength={32}
          />

          <Box width={"80%"}>
            <BlFormInput
              name={"description"}
              label={t(PageResources.Description.Label)}
              placeholder={t(PageResources.Description.Placeholder)}
              control={control}
              errors={errors}
              maxLength={128}
            />
          </Box>

          <Typography variant="h2">
            {t(PageResources.Services.Title)}
          </Typography>

          <Typography variant="subtitle2" fontStyle={"normal"} mb={2}>
            {t(PageResources.Services.Description)}
          </Typography>

          <Grid container spacing={2} mb={2}>
            {watch("services").map((e, i) => (
              <Grid item key={i} xs={6}>
                <StyledFlex $gap={0.5} $alignItems="center">
                  <BlCheckboxInput
                    name={`services.${i}.isSelected`}
                    label={watch("services")[i].name}
                    control={control}
                    errors={errors}
                  />
                  {watch("services")[i].description && (
                    <BlDefaultTooltip
                      title={watch("services")[i].description}
                    />
                  )}
                </StyledFlex>
              </Grid>
            ))}
          </Grid>

          <StyledValidationText data-name={"services.root"}>
            {errors.services?.message ?? errors.services?.root?.message}
          </StyledValidationText>

          <Typography variant="h2">
            {t(PageResources.Variants.Title)}
          </Typography>

          <StyledFlex $gap={1}>
            <Typography variant="subtitle2" fontStyle={"normal"}>
              {t(PageResources.Variants.Description)}
            </Typography>
          </StyledFlex>

          <StyledFlex $marginTop={2} $marginBottom={1}>
            <Typography variant="subtitle2" fontStyle={"normal"}>
              {t(Resources.Settings.Services.PriceColumns.Description)}
            </Typography>
          </StyledFlex>

          <StyledPriceGrid $labelColumnWidth={200}>
            <PriceColumnsHeader taxRate={watch("variants")[0]?.taxRate ?? 0} />

            {watch("variants").map((variant, i) => (
              <React.Fragment key={variant.name}>
                <Box>
                  <BlCheckboxInput
                    name={`variants.${i}.isSelected`}
                    label={variant.name}
                    control={control}
                    errors={errors}
                  />
                </Box>

                {variant.isSelected ? (
                  <PriceColumnsRow
                    control={control}
                    errors={errors}
                    netFieldName={`variants.${i}.priceWithoutTax`}
                    grossFieldName={`variants.${i}.priceWithTax`}
                    taxRate={variant.taxRate}
                    maxNet={MAX_PRICE_WITHOUT_TAX}
                    onNetChange={() => onPriceWithoutTaxChange(i)}
                    onGrossChange={() => onPriceWithTaxChange(i)}
                  />
                ) : (
                  <>
                    <div />
                    <div />
                    <div />
                  </>
                )}
              </React.Fragment>
            ))}
          </StyledPriceGrid>

          <StyledValidationText data-name={"variants.root"}>
            {errors.variants?.root?.message}
          </StyledValidationText>
        </form>

        <Grid container gap={2} mt={4}>
          <Grid item>
            <BlButton color="primary" onClick={onSubmit} isLoading={isSaving}>
              {t(PageResources.Save)}
            </BlButton>
          </Grid>
          <Grid
            item
            visibility={servicePackage === null ? "hidden" : "inherit"}
          >
            <BlConfirmButton
              variant="secondary"
              tooltip={t(Resources.Common.Delete)}
              modalTitle={t(
                Resources.Settings.ServicePackages.List.Delete.ModalTitle,
              )}
              modalContent={t(
                Resources.Settings.ServicePackages.List.Delete.ModalContent,
              )}
              onResult={result => {
                if (result) {
                  if (!!servicePackage?.servicePublicID) {
                    dispatch(
                      deleteServicePackageAsync.request({
                        servicePublicID: servicePackage.servicePublicID,
                      }),
                    );
                  }
                  navigate(
                    getPath(
                      AppRouting.Settings,
                      t(Resources.Settings.ServicePackages.Url),
                    ),
                  );
                }
              }}
            >
              {t(PageResources.Delete.Button)}
            </BlConfirmButton>
          </Grid>
        </Grid>
      </FormWrapper>
    </StyledContentWrapper>
  );
};

function getVariants(): VariantFormModel[] {
  return [
    {
      name: "Normal",
      taxRate: 0.21,
      isSelected: true,
      priceWithoutTax: 0,
      frequency: ServiceVariantFrequency.Single,
    },
    {
      name: "Normal / měsíční",
      taxRate: 0.21,
      isSelected: false,
      priceWithoutTax: 0,
      frequency: ServiceVariantFrequency.Monthly,
    },
    {
      name: "Premium",
      taxRate: 0.21,
      isSelected: false,
      priceWithoutTax: 0,
      frequency: ServiceVariantFrequency.Single,
    },
    {
      name: "Premium / měsíční",
      taxRate: 0.21,
      isSelected: false,
      priceWithoutTax: 0,
      frequency: ServiceVariantFrequency.Monthly,
    },
    {
      name: "VIP",
      taxRate: 0.21,
      isSelected: false,
      priceWithoutTax: 0,
      frequency: ServiceVariantFrequency.Single,
    },
    {
      name: "VIP / měsíční",
      taxRate: 0.21,
      isSelected: false,
      priceWithoutTax: 0,
      frequency: ServiceVariantFrequency.Monthly,
    },
  ];
}
